import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { GEO_SERVICES } from '../../content/geo-content';

export interface SeoData {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: 'website' | 'article';
  robots?: string;
  breadcrumbs?: Array<{ name: string; path: string }>;
  structuredData?: Array<Record<string, unknown>>;
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private readonly siteUrl = 'https://manavaimmobilier.pf';
  private readonly siteName = 'Manava Immobilier';
  private readonly defaultImage = `${this.siteUrl}/assets/img/cover/moorea-island.webp`;
  private readonly structuredDataId = 'seo-structured-data';
  private currentGraph: Array<Record<string, unknown>> = [];

  constructor(
    private readonly title: Title,
    private readonly meta: Meta,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  updatePage(data: SeoData): void {
    const canonicalUrl = this.absoluteUrl(data.path);
    const imageUrl = this.absoluteUrl(data.image ?? this.defaultImage);
    this.title.setTitle(data.title);
    this.updateName('description', data.description);
    this.updateName('robots', data.robots ?? 'index, follow, max-image-preview:large');
    this.updateName('author', this.siteName);
    this.updatePropertyMeta('og:locale', 'fr_PF');
    this.updatePropertyMeta('og:site_name', this.siteName);
    this.updatePropertyMeta('og:type', data.type ?? 'website');
    this.updatePropertyMeta('og:title', data.title);
    this.updatePropertyMeta('og:description', data.description);
    this.updatePropertyMeta('og:url', canonicalUrl);
    this.updatePropertyMeta('og:image', imageUrl);
    this.updatePropertyMeta('og:image:alt', data.title);
    this.updateName('twitter:card', 'summary_large_image');
    this.updateName('twitter:title', data.title);
    this.updateName('twitter:description', data.description);
    this.updateName('twitter:image', imageUrl);
    this.updateCanonical(canonicalUrl);
    this.updateAlternate(canonicalUrl);
    this.currentGraph = [
      ...this.buildPageGraph(data, canonicalUrl, imageUrl),
      ...(data.structuredData ?? [])
    ];
    this.updateStructuredData(this.currentGraph);
  }

  updateProperty(property: Record<string, unknown>, productRef: string): void {
    const titleText = this.cleanText(property['title']) || `Bien immobilier ${productRef}`;
    const fullDescription = this.cleanText(property['details_listing'] ?? property['details_print'] ?? property['details'] ?? property['address']) || `Découvrez ce bien immobilier proposé par Manava Immobilier en Polynésie française.`;
    const description = this.truncate(fullDescription, 160);
    const path = `/properties/${encodeURIComponent(productRef)}`;
    const canonicalUrl = this.absoluteUrl(path);
    const images = Array.isArray(property['images_public'])
      ? property['images_public']
        .filter((image): image is string => typeof image === 'string')
        .map(image => this.absoluteUrl(image))
      : [];
    const imageUrl = this.absoluteUrl(images[0] ?? this.defaultImage);
    const pageTitle = `${titleText} | Manava Immobilier`;
    this.updatePage({
      title: pageTitle,
      description,
      path,
      image: imageUrl,
      type: 'article',
      breadcrumbs: [
        { name: 'Accueil', path: '/' },
        { name: 'Biens immobiliers', path: '/properties' },
        { name: titleText, path }
      ]
    });
    const price = property['price'] ?? property['rent'];
    const offer = price && Number(price) > 0 ? {
      '@type': 'Offer',
      price: Number(price),
      priceCurrency: 'XPF',
      availability: property['state'] === 3 ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      url: canonicalUrl
    } : undefined;
    const graph = this.buildPageGraph({
      title: pageTitle,
      description,
      path,
      image: imageUrl,
      type: 'article',
      breadcrumbs: [
        { name: 'Accueil', path: '/' },
        { name: 'Biens immobiliers', path: '/properties' },
        { name: titleText, path }
      ]
    }, canonicalUrl, imageUrl);
    const listingId = `${canonicalUrl}#listing`;
    const webpage = graph.find(node => node['@id'] === `${canonicalUrl}#webpage`);
    if (webpage) {
      webpage['mainEntity'] = { '@id': listingId };
    }
    const surface = property['surface_living_space'] ?? property['surface_living'] ?? property['surface'];
    graph.push({
      '@type': 'RealEstateListing',
      '@id': listingId,
      name: titleText,
      description: this.truncate(fullDescription, 500),
      url: canonicalUrl,
      image: images.length > 0 ? images : [imageUrl],
      identifier: productRef,
      datePosted: property['created_at'] ?? property['published_at'] ?? undefined,
      dateModified: property['updated_at'] ?? undefined,
      offers: offer,
      about: {
        '@type': 'Residence',
        name: titleText,
        address: this.cleanText(property['address'])
          ? {
              '@type': 'PostalAddress',
              streetAddress: this.cleanText(property['address']),
              addressCountry: 'PF'
            }
          : undefined,
        numberOfRooms: property['rooms'] ?? undefined,
        numberOfBedrooms: property['bedrooms'] ?? undefined,
        floorSize: surface
          ? {
              '@type': 'QuantitativeValue',
              value: Number(surface),
              unitCode: 'MTK'
            }
          : undefined
      },
      provider: { '@id': `${this.siteUrl}/#agency` }
    });
    this.currentGraph = graph;
    this.updateStructuredData(graph);
  }

  updatePropertyCollection(properties: Array<Record<string, unknown>>, total?: number): void {
    const canonicalUrl = this.absoluteUrl('/properties');
    const itemListId = `${canonicalUrl}#list`;
    const items = properties
      .filter(property => typeof property['product_ref'] === 'string' || typeof property['product_ref'] === 'number')
      .map((property, index) => {
        const productRef = String(property['product_ref']);
        const url = this.absoluteUrl(`/properties/${encodeURIComponent(productRef)}`);
        const name = this.cleanText(property['title']) || `Bien immobilier ${productRef}`;
        const price = property['price'] ?? property['rent'];
        const image = Array.isArray(property['images_public'])
          ? property['images_public'].find((value): value is string => typeof value === 'string')
          : undefined;
        return {
          '@type': 'ListItem',
          position: index + 1,
          url,
          item: {
            '@type': 'RealEstateListing',
            '@id': `${url}#listing`,
            name,
            url,
            image: image ? this.absoluteUrl(image) : undefined,
            offers: price && Number(price) > 0
              ? {
                  '@type': 'Offer',
                  price: Number(price),
                  priceCurrency: 'XPF',
                  availability: property['state'] === 3
                    ? 'https://schema.org/SoldOut'
                    : 'https://schema.org/InStock'
                }
              : undefined,
            provider: { '@id': `${this.siteUrl}/#agency` }
          }
        };
      });
    const graph = this.currentGraph.filter(node => node['@id'] !== itemListId);
    const webpage = graph.find(node => node['@id'] === `${canonicalUrl}#webpage`);
    if (webpage) {
      webpage['@type'] = 'CollectionPage';
      webpage['mainEntity'] = { '@id': itemListId };
    }
    graph.push({
      '@type': 'ItemList',
      '@id': itemListId,
      name: 'Biens immobiliers proposés par Manava Immobilier',
      numberOfItems: total ?? items.length,
      itemListElement: items
    });
    this.currentGraph = graph;
    this.updateStructuredData(graph);
  }

  private buildPageGraph(data: SeoData, canonicalUrl: string, imageUrl: string): Array<Record<string, unknown>> {
    const graph: Array<Record<string, unknown>> = [{
      '@type': 'RealEstateAgent',
      '@id': `${this.siteUrl}/#agency`,
      name: this.siteName,
      legalName: 'SARL GROUPE PACIFIC IMMO',
      alternateName: 'MANAVA : L’Agence Immobilière du Pacifique',
      description: 'Agence immobilière locale établie à Papetoai, Moorea, spécialisée dans l’achat, la vente, la location et l’estimation de biens en Polynésie française.',
      slogan: 'L’Agence Immobilière du Pacifique',
      url: `${this.siteUrl}/`,
      logo: `${this.siteUrl}/assets/logo/logo-540px.webp`,
      image: imageUrl,
      telephone: '+68987701670',
      email: 'contact@manavaimmobilier.pf',
      currenciesAccepted: 'XPF',
      identifier: {
        '@type': 'PropertyValue',
        propertyID: 'Numéro TAHITI',
        value: 'G14583'
      },
      legalRepresentative: {
        '@type': 'Person',
        name: 'Tevainui ALVES',
        jobTitle: 'Gérante'
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'PK 23,100 côté mer',
        addressLocality: 'Papetoai',
        addressRegion: 'Moorea',
        addressCountry: 'PF'
      },
      areaServed: [
        { '@type': 'AdministrativeArea', name: 'Polynésie française' },
        { '@type': 'Place', name: 'Tahiti' },
        { '@type': 'Place', name: 'Moorea' }
      ],
      knowsAbout: [
        'achat immobilier',
        'vente immobilière',
        'location immobilière',
        'estimation immobilière',
        'immobilier à Tahiti',
        'immobilier à Moorea',
        'marché immobilier en Polynésie française'
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'service client',
        telephone: '+68987701670',
        email: 'contact@manavaimmobilier.pf',
        availableLanguage: 'fr'
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Services immobiliers',
        itemListElement: GEO_SERVICES.map(service => ({
          '@type': 'Offer',
          itemOffered: { '@id': `${this.siteUrl}/services#${service.id}` }
        }))
      }
    }, {
      '@type': 'WebSite',
      '@id': `${this.siteUrl}/#website`,
      url: `${this.siteUrl}/`,
      name: this.siteName,
      inLanguage: 'fr-PF',
      publisher: { '@id': `${this.siteUrl}/#agency` }
    }, {
      '@type': 'WebPage',
      '@id': `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: data.title,
      description: data.description,
      inLanguage: 'fr-PF',
      isPartOf: { '@id': `${this.siteUrl}/#website` },
      about: { '@id': `${this.siteUrl}/#agency` },
      primaryImageOfPage: { '@type': 'ImageObject', url: imageUrl }
    }];
    graph.push(...GEO_SERVICES.map(service => ({
      '@type': 'Service',
      '@id': `${this.siteUrl}/services#${service.id}`,
      name: service.name,
      description: service.description,
      url: `${this.siteUrl}/services`,
      provider: { '@id': `${this.siteUrl}/#agency` },
      areaServed: { '@type': 'AdministrativeArea', name: 'Polynésie française' }
    })));
    if (data.breadcrumbs?.length) {
      graph.push({
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumb`,
        itemListElement: data.breadcrumbs.map((breadcrumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: breadcrumb.name,
          item: this.absoluteUrl(breadcrumb.path)
        }))
      });
    }
    return graph;
  }

  private updateName(name: string, content: string): void {
    this.meta.updateTag({ name, content }, `name="${name}"`);
  }

  private updatePropertyMeta(property: string, content: string): void {
    this.meta.updateTag({ property, content }, `property="${property}"`);
  }

  private updateCanonical(url: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = url;
  }

  private updateAlternate(url: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="alternate"][hreflang="fr-PF"]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = 'fr-PF';
      this.document.head.appendChild(link);
    }
    link.href = url;
  }

  private updateStructuredData(graph: Array<Record<string, unknown>>): void {
    this.document.getElementById(this.structuredDataId)?.remove();
    const script = this.document.createElement('script');
    script.id = this.structuredDataId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': graph
    }, (_key, value) => value === undefined ? undefined : value);
    this.document.head.appendChild(script);
  }

  private absoluteUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) {
      return pathOrUrl;
    }
    const normalizedPath = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${this.siteUrl}${normalizedPath}`;
  }

  private cleanText(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }
    const element = this.document.createElement('div');
    element.innerHTML = value;
    return (element.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  private truncate(value: string, maximumLength: number): string {
    if (value.length <= maximumLength) {
      return value;
    }
    return `${value.slice(0, maximumLength - 1).trimEnd()}…`;
  }
}
